'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import ProcessOverviewThreeStepRedesign from './variants/Redesign';

export type ProcessOverviewThreeStepProps =
  SliceComponentProps<Content.ProcessOverviewThreeStepSlice>;

type ProcessOverviewThreeStepCurrentProps = SliceComponentProps<
  Exclude<
    Content.ProcessOverviewThreeStepSlice,
    { variation: 'redesignCarousel' }
  >
>;

const ProcessOverviewThreeStep: FC<ProcessOverviewThreeStepProps> = ({
  slice,
  ...rest
}) => {
  if (slice.variation === 'redesignCarousel') {
    return <ProcessOverviewThreeStepRedesign slice={slice} {...rest} />;
  }
  return <ProcessOverviewThreeStepCurrent slice={slice} {...rest} />;
};

const PURPLE = '#4B0FEA';

type StepWithTakeaway = {
  phase_title?: string;
  phase_icon?: import('@prismicio/client').ImageField;
  phase_description?: import('@prismicio/client').RichTextField;
  phase_highlight?: string;
  phase_takeaway?: import('@prismicio/client').LinkField;
};

const ProcessOverviewThreeStepCurrent: FC<
  ProcessOverviewThreeStepCurrentProps
> = ({ slice }) => {
  const { tagline, title, description, center_icon, steps, background_image } =
    slice.primary;

  const headerRef = useRef<HTMLDivElement>(null);
  const diagramRef = useRef<HTMLDivElement>(null);

  const isVelx = (slice.variation as string) === 'velx';

  useEffect(() => {
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReduced) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: headerRef, delay: '0ms' },
      { ref: diagramRef, delay: '300ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, []);

  const stepLeft = steps[0] as StepWithTakeaway | undefined;
  const stepRight = steps[1] as StepWithTakeaway | undefined;
  const stepBottom = steps[2] as StepWithTakeaway | undefined;

  // Theme tokens
  const sectionCls = isVelx
    ? 'relative overflow-hidden bg-[#FAFAFA] px-4 pb-[80px] pt-[60px] font-inter md:px-8 lg:px-[150px]'
    : 'relative mx-[30px] overflow-hidden rounded-[24px] bg-[#041118] px-4 pb-[80px] pt-[60px] md:px-8 lg:px-[150px]';

  const taglineCls = isVelx
    ? 'inline-flex rounded-full border px-[16px] py-[8px] text-[14px] font-normal uppercase leading-[1.2]'
    : 'inline-flex rounded-full border border-[#00f557] px-[16px] py-[8px] text-[14px] font-normal uppercase leading-[1.2] text-white';

  const titleCls = isVelx
    ? 'max-w-[868px] text-[32px] font-semibold leading-[1.2] text-[#041118] md:text-[42px]'
    : 'max-w-[868px] text-[32px] font-semibold leading-[1.2] text-white md:text-[42px]';

  const descCls = isVelx
    ? 'max-w-[868px] text-[16px] font-normal leading-[1.4] text-[#041118]/75'
    : 'max-w-[868px] text-[16px] font-normal leading-[1.4] text-white';

  const phaseTitleCls = isVelx
    ? 'm-0 text-[24px] font-semibold'
    : 'text-[24px] font-semibold text-[#00f557]';

  const phaseDescCls = isVelx
    ? 'text-[16px] font-light leading-[1.5] text-[#041118]/75'
    : 'text-[16px] font-light leading-[1.5] text-white';

  const orbitOuterBorder = isVelx
    ? 'rgba(75, 15, 234, 0.18)'
    : 'rgba(255,255,255,0.1)';
  const orbitInnerBorder = isVelx
    ? 'rgba(75, 15, 234, 0.12)'
    : 'rgba(255,255,255,0.07)';

  const renderTakeaway = (step?: StepWithTakeaway) =>
    isVelx && step && isFilled.link(step.phase_takeaway) ? (
      <div className="mt-4">
        <PrismicNextLink
          field={step.phase_takeaway}
          className="inline-flex items-center gap-2 rounded-full border border-[#cccccc] bg-white px-5 py-2.5 text-[14px] font-medium leading-[1.4] text-[#041118] transition-colors hover:border-[#041118] hover:bg-[#f4f4f4]"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 8h10" />
            <path d="M9 4l4 4-4 4" />
          </svg>
          {(step.phase_takeaway as { text?: string }).text}
        </PrismicNextLink>
      </div>
    ) : null;

  const renderHighlight = (step?: StepWithTakeaway) =>
    isVelx && step?.phase_highlight ? (
      <p className="mt-2 text-[20px] font-normal leading-[1.5] text-[#041118]">
        {step.phase_highlight}
      </p>
    ) : null;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={sectionCls}
      style={
        isFilled.image(background_image)
          ? {
              backgroundImage: `url(${background_image.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : undefined
      }
    >
      <div
        ref={headerRef}
        className="flex flex-col items-center gap-[24px] text-center"
      >
        {tagline && (
          <span
            className={taglineCls}
            style={isVelx ? { borderColor: PURPLE, color: PURPLE } : undefined}
          >
            {tagline}
          </span>
        )}

        {isFilled.richText(title) && (
          <PrismicRichText
            field={title}
            components={{
              heading2: ({ children }) => (
                <h2 className={titleCls}>{children}</h2>
              ),
              strong: ({ children }) =>
                isVelx ? (
                  <strong className="font-semibold" style={{ color: PURPLE }}>
                    {children}
                  </strong>
                ) : (
                  <strong className="font-semibold text-[#00f557]">
                    {children}
                  </strong>
                ),
            }}
          />
        )}

        {isFilled.richText(description) && (
          <PrismicRichText
            field={description}
            components={{
              paragraph: ({ children }) => (
                <p className={descCls}>{children}</p>
              ),
            }}
          />
        )}
      </div>

      <div
        ref={diagramRef}
        className="relative mx-auto mt-[30px] w-full max-w-[1080px]"
      >
        <div className="hidden lg:block">
          <div className="relative mx-auto" style={{ height: 722 }}>
            <div className="absolute left-1/2 top-[20px] -translate-x-1/2">
              <div
                className="animate-orbit-pulse absolute left-1/2 top-1/2 size-[440px] -translate-x-1/2 -translate-y-1/2 rounded-full border"
                style={{ borderColor: orbitOuterBorder }}
              />
              <div
                className="animate-orbit-pulse-delayed absolute left-1/2 top-1/2 size-[370px] -translate-x-1/2 -translate-y-1/2 rounded-full border"
                style={{ borderColor: orbitInnerBorder }}
              />
              {isFilled.image(center_icon) && (
                <PrismicNextImage
                  field={center_icon}
                  className="relative size-[305px] object-contain"
                  fallbackAlt=""
                />
              )}
            </div>

            {stepLeft && (
              <div className="absolute left-0 top-[105px] flex w-[292px] flex-col gap-[8px] text-right leading-[1.5]">
                {stepLeft.phase_title && (
                  <p
                    className={phaseTitleCls}
                    style={isVelx ? { color: PURPLE } : undefined}
                  >
                    {stepLeft.phase_title}
                  </p>
                )}
                {isFilled.richText(stepLeft.phase_description) && (
                  <PrismicRichText
                    field={stepLeft.phase_description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className={phaseDescCls}>{children}</p>
                      ),
                    }}
                  />
                )}
                {renderHighlight(stepLeft)}
                {renderTakeaway(stepLeft)}
              </div>
            )}
            {stepLeft && isFilled.image(stepLeft.phase_icon) && (
              <PrismicNextImage
                field={stepLeft.phase_icon}
                className="absolute left-[260px] top-[50px] size-[180px] object-contain"
                fallbackAlt=""
              />
            )}

            {stepRight && (
              <div className="absolute right-0 top-[105px] flex w-[292px] flex-col gap-[8px] text-left leading-[1.5]">
                {stepRight.phase_title && (
                  <p
                    className={phaseTitleCls}
                    style={isVelx ? { color: PURPLE } : undefined}
                  >
                    {stepRight.phase_title}
                  </p>
                )}
                {isFilled.richText(stepRight.phase_description) && (
                  <PrismicRichText
                    field={stepRight.phase_description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className={phaseDescCls}>{children}</p>
                      ),
                    }}
                  />
                )}
                {renderHighlight(stepRight)}
                {renderTakeaway(stepRight)}
              </div>
            )}
            {stepRight && isFilled.image(stepRight.phase_icon) && (
              <PrismicNextImage
                field={stepRight.phase_icon}
                className="absolute right-[260px] top-[50px] size-[180px] object-contain"
                fallbackAlt=""
              />
            )}

            {stepBottom && isFilled.image(stepBottom.phase_icon) && (
              <PrismicNextImage
                field={stepBottom.phase_icon}
                className="absolute left-1/2 top-[340px] size-[180px] -translate-x-1/2 object-contain"
                fallbackAlt=""
              />
            )}
            {stepBottom && (
              <div className="absolute left-1/2 top-[480px] flex w-[342px] -translate-x-1/2 flex-col items-center gap-[8px] text-center leading-[1.5]">
                {stepBottom.phase_title && (
                  <p
                    className={phaseTitleCls}
                    style={isVelx ? { color: PURPLE } : undefined}
                  >
                    {stepBottom.phase_title}
                  </p>
                )}
                {isFilled.richText(stepBottom.phase_description) && (
                  <PrismicRichText
                    field={stepBottom.phase_description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className={phaseDescCls}>{children}</p>
                      ),
                    }}
                  />
                )}
                {renderHighlight(stepBottom)}
                {renderTakeaway(stepBottom)}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-[48px] pt-[40px] lg:hidden">
          {isFilled.image(center_icon) && (
            <div className="relative">
              <div
                className="animate-orbit-pulse absolute left-1/2 top-1/2 size-[260px] -translate-x-1/2 -translate-y-1/2 rounded-full border"
                style={{ borderColor: orbitOuterBorder }}
              />
              <div
                className="animate-orbit-pulse-delayed absolute left-1/2 top-1/2 size-[220px] -translate-x-1/2 -translate-y-1/2 rounded-full border"
                style={{ borderColor: orbitInnerBorder }}
              />
              <PrismicNextImage
                field={center_icon}
                className="relative size-[180px] object-contain"
                fallbackAlt=""
              />
            </div>
          )}

          {(steps as StepWithTakeaway[]).map((step, index) => (
            <div
              key={index}
              className="flex max-w-[342px] flex-col items-center gap-[12px] text-center"
            >
              {isFilled.image(step.phase_icon) && (
                <PrismicNextImage
                  field={step.phase_icon}
                  className="size-[88px] object-contain"
                  fallbackAlt=""
                />
              )}
              {step.phase_title && (
                <p
                  className={
                    isVelx
                      ? 'm-0 text-[20px] font-semibold leading-[1.5] md:text-[24px]'
                      : 'text-[20px] font-semibold leading-[1.5] text-[#00f557] md:text-[24px]'
                  }
                  style={isVelx ? { color: PURPLE } : undefined}
                >
                  {step.phase_title}
                </p>
              )}
              {isFilled.richText(step.phase_description) && (
                <PrismicRichText
                  field={step.phase_description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className={phaseDescCls}>{children}</p>
                    ),
                  }}
                />
              )}
              {renderHighlight(step)}
              {isVelx && isFilled.link(step.phase_takeaway) && (
                <div className="mt-2">
                  <PrismicNextLink
                    field={step.phase_takeaway}
                    className="inline-flex items-center gap-2 rounded-full border border-[#4B0FEA] bg-transparent px-5 py-2.5 text-[14px] font-semibold leading-[1.4] text-[#4B0FEA] transition-colors hover:bg-[#4B0FEA] hover:text-white"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessOverviewThreeStep;
